import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { defineMessages } from 'react-intl';

import {
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  MainAsideLayout,
  Section,
} from '@sorare/core/src/components/layout/MainAsideLayout';
import { LastSales } from '@sorare/core/src/components/seo/Player/LastSales';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';

import { PriceGraph } from 'components/price/PriceGraph';
import { PriceHistoryFromProps } from 'components/price/PriceHistoryFromProps';

import { TransactionsFilters } from './TransactionsFilters';
import { Transactions_anyPlayerInterface } from './__generated__/index.graphql';

const messages = defineMessages({
  noSupply: {
    id: 'Seo.Player.Supply.noSupply',
    defaultMessage: 'No supply for this player',
  },
});

type Props = {
  anyPlayer: Transactions_anyPlayerInterface;
};

type FilterableRarity = Rarity.limited | Rarity.rare | Rarity.super_rare;

const filterableRarities = [Rarity.limited, Rarity.rare, Rarity.super_rare];

type Filters = {
  rarity: FilterableRarity;
  seasonEligibility?: SeasonEligibility;
};

export const Transactions = ({ anyPlayer }: Props) => {
  const { up: isLaptopAndAbove } = useScreenSize('laptop');

  const [filters, setFilters] = useState<Filters>({
    rarity: Rarity.limited,
    seasonEligibility: SeasonEligibility.IN_SEASON,
  });

  const transactionsFilters = (
    <TransactionsFilters
      rarity={filters.rarity}
      filterableRarities={filterableRarities}
      onRarityChange={rarity =>
        filterableRarities.includes(rarity) &&
        setFilters({ ...filters, rarity: rarity as FilterableRarity })
      }
      onSeasonEligibilityChange={seasonEligibility =>
        setFilters({ ...filters, seasonEligibility })
      }
      seasonEligibility={filters.seasonEligibility}
    />
  );
  if (!anyPlayer.slug) return null;

  const blocks = {
    priceGraph: (
      <PriceGraph
        withSeasonEligibility
        seasonEligibility={filters.seasonEligibility}
        rarity={filters.rarity}
        playerSlug={anyPlayer.slug}
      />
    ),
    lastSales: (
      <LastSales
        withSeasonEligibility
        seasonEligibility={filters.seasonEligibility}
        rarity={filters.rarity}
        tokenHistory={(rarity, seasonEligibility) => (
          <PriceHistoryFromProps
            variables={{
              playerSlug: anyPlayer.slug,
              rarity,
              seasonEligibility,
            }}
            count={5}
            inBlock={false}
          />
        )}
      />
    ),
  };

  if (!anyPlayer.cardSupply.length) {
    return (
      <>
        <NoIndex />
        <Section topPadding>
          <FullWidthContainer>
            <Warning variant="yellow" title={messages.noSupply} />
          </FullWidthContainer>
        </Section>
      </>
    );
  }

  if (isLaptopAndAbove) {
    return (
      <>
        <NoIndex />
        {transactionsFilters}
        <MainAsideLayout aside={[blocks.lastSales]}>
          {blocks.priceGraph}
        </MainAsideLayout>
      </>
    );
  }

  return (
    <>
      <NoIndex />
      {transactionsFilters}
      <Section topPadding>
        {blocks.priceGraph}
        {blocks.lastSales}
      </Section>
    </>
  );
};

Transactions.fragments = {
  anyPlayer: gql`
    fragment Transactions_anyPlayerInterface on AnyPlayerInterface {
      slug
      cardSupply {
        limited
        rare
        superRare
        unique
      }
    }
  ` as TypedDocumentNode<Transactions_anyPlayerInterface>,
};
